import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Add from "@mui/icons-material/Add";
import FolderSpecial from "@mui/icons-material/FolderSpecial";
import ContentCopy from "@mui/icons-material/ContentCopy";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import debounce from "lodash.debounce";
import ModuleCardPreview from "../ModuleCardPreview";
import SectionForm from "./SectionForm";
import InvestmentReadinessQuestions from "./InvestmentReadinessQuestions";
import ExitReadinessQuestions from "./ExitReadinessQuestions";
import InvestorLeadSection from "./InvestorLeadSection";
import ImageUploader from "./ImageUploader.jsx";
import CertificationRulesBuilder from "./CertificationRulesBuilder.jsx";
import ExcelUpload from "./ExcelUpload";
import ExcelVariantModal from "./ExcelVariantModal";
import { useDashboardCards } from "../../DashboardCard/useDashboardCards";
import { Delete } from "@mui/icons-material";
import socket, { deleteRequest, postRequest, showMessage, userId as socketUserId } from "../../../../config";
import { customOptionPrimaryLabel } from "./customOptionHelpers";
import { toggleModal } from "../../../../reducers/modalReducer";
import ModalWrapper from "../../../common/Modal/ModalWrapper";
import Button from "../../../common/Button";

/** Company objectives (tabs) – same as Company Objective in company settings */
const COMPANY_OBJECTIVES = ['Investment', 'Growth', 'Exit'];
/** Subscription tiers for section tabs (Free, Lite, Pro; All for "all tiers") */
const SUBSCRIPTION_TIERS = ['Free', 'Lite', 'Pro'];
const OBJECTIVE_ALL = 'All';
const TIER_ALL = 'All';

const getVariantKey = (objective, tier) => `${objective}-${tier}`;

/** Resolve variant key string back to objective + tier (for sectionAccessRestrictions payload). */
function parseVariantKey(variantKey) {
    for (const obj of COMPANY_OBJECTIVES) {
        for (const tier of SUBSCRIPTION_TIERS) {
            if (getVariantKey(obj, tier) === variantKey) {
                return { objective: obj, tier };
            }
        }
    }
    if (variantKey === getVariantKey(OBJECTIVE_ALL, TIER_ALL)) {
        return { objective: OBJECTIVE_ALL, tier: TIER_ALL };
    }
    return null;
}

const emptySectionRestrictions = () => {
    const o = {};
    COMPANY_OBJECTIVES.forEach((obj) => {
        SUBSCRIPTION_TIERS.forEach((tier) => {
            o[getVariantKey(obj, tier)] = false;
        });
    });
    o[getVariantKey(OBJECTIVE_ALL, TIER_ALL)] = false;
    return o;
};

const moduleFormSchema = yup.object().shape({
    moduleName: yup
        .string()
        .required('Module name is required')
        .min(3, 'Module name must be at least 3 characters')
        .max(100, 'Module name cannot exceed 100 characters'),
    moduleKey: yup
        .string()
        .required('Module key is required')
        .min(2, 'Module key must be at least 2 characters')
        .max(16, 'Module key cannot exceed 16 characters')
        .matches(/^[A-Z0-9]+$/, 'Module key can only contain uppercase letters and numbers'),
    slug: yup.string(),
    moduleSubTitle: yup
        .string()
        .max(100, 'Subtitle cannot exceed 100 characters'),
    moduleTagColor: yup
        .string()
        .transform((v) => (v == null ? '' : String(v).trim()))
        .max(7, 'Colour value is too long')
        .matches(/^$|^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, 'Use a hex colour like #4A67FF or leave blank'),
    moduleDescription: yup
        .string()
        .max(500, 'Description cannot exceed 500 characters'),
    imageURL: yup
        .string()
        .nullable(),
    type: yup
        .string()
        .required('Module type is required')
        .oneOf(['common', 'unique', 'admin', 'partner', 'internal', 'external'], 'Invalid module type'),
    dashboardCardId: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value)),
    isActive: yup
        .boolean()
        .required('Active status is required'),
    isReportModule: yup.boolean(),
    isCustomSection: yup.boolean(),
    isDirectorsModule: yup.boolean(),
    isPaid: yup.boolean(),
    price: yup.string().nullable().when('isPaid', {
        is: true,
        then: (schema) => schema.required('Price amount is required when module is paid'),
        otherwise: (schema) => schema.optional().nullable()
    })
});

const ModuleForm = ({ moduleData, onFormChange, onSubmit, onCancel, isEdit = false }) => {
    const dispatch = useDispatch();
    const DELETE_DEBOUNCE_MS = 3000;
    const deleteLocksRef = useRef(new Map());
    const deleteUnlockersRef = useRef(new Map());
    const sectionDeleteQueueRef = useRef(new Map());
    // Track which existing sections (by _id) have been modified
    const dirtySectionIdsRef = useRef(new Set());
    // When moduleKey changes, all section numbers update so every section must be saved
    const allSectionsDirtyRef = useRef(false);
    const [pendingSectionDeleteKeys, setPendingSectionDeleteKeys] = useState(new Set());
    // Sections grouped by Company Objective + Subscription Tier (e.g. "Investment-Free")
    const emptySectionsByVariant = () => {
        const o = {};
        COMPANY_OBJECTIVES.forEach(obj => {
            SUBSCRIPTION_TIERS.forEach(tier => {
                o[getVariantKey(obj, tier)] = [];
            });
        });
        o[getVariantKey(OBJECTIVE_ALL, TIER_ALL)] = []; // Sections that apply to all
        return o;
    };
    const [sectionsByVariant, setSectionsByVariant] = useState(emptySectionsByVariant());
    // Per-objective/per-tier dashboard card placement
    const [placementsByVariant, setPlacementsByVariant] = useState({});
    /** Per objective+tier: when true, sections for that combo are not exposed to end users */
    const [sectionRestrictionsByVariant, setSectionRestrictionsByVariant] = useState(emptySectionRestrictions());
    const [sectionErrors, setSectionErrors] = useState({}); // key: variantKey-sectionIndex
    const [expandedSectionIndex, setExpandedSectionIndex] = useState(0);
    const [isModuleKeyManuallyEdited, setIsModuleKeyManuallyEdited] = useState(false);
    const [tab, setTab] = useState('Repository');
    const [selectedObjective, setSelectedObjective] = useState('Investment');
    const [selectedTier, setSelectedTier] = useState('Free');
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [cloneMode, setCloneMode] = useState('bulk'); // 'bulk' | 'single'
    const [cloneSection, setCloneSection] = useState(null); // for single section clone
    const [cloneTargets, setCloneTargets] = useState([]); // selected variant keys to clone into
    const [cloneView, setCloneView] = useState('select'); // 'select' | 'conflict'
    const [cloneConflictConflicts, setCloneConflictConflicts] = useState([]); // [{ sectionNo, objective, tier }]
    const { cards: dashboardCards } = useDashboardCards();
    const [certificationRules, setCertificationRules] = useState(
        Array.isArray(moduleData?.certificationRules) ? moduleData.certificationRules : []
    );

    const [isPriorityLoading, setIsPriorityLoading] = useState(false);
    const [hasPrioritiesAlreadySet, setHasPrioritiesAlreadySet] = useState(false);
    const [jobSummary, setJobSummary] = useState(null);
    const [sectionProgress, setSectionProgress] = useState({});
    const [sectionOrder, setSectionOrder] = useState([]);
    const moduleIdRef = useRef(moduleData?.['_id']);
    moduleIdRef.current = moduleData?.['_id'];
    const toastedPriorityRef = useRef(false);
    const [isSocketConnected, setIsSocketConnected] = useState(() => !!(socket && socket.connected));

    useEffect(() => {
        if (!socket) return;

        const isThisModule = (payload) => {
            if (!payload?.['moduleId'] || !moduleIdRef.current) return true;
            return String(payload['moduleId']) === String(moduleIdRef.current);
        };

        const applySections = (sections) => {
            if (!Array.isArray(sections) || !sections.length) return;
            const next = {};
            const order = [];
            sections.forEach(s => {
                if (!s || !s['sectionId']) return;
                const total = s['total'] || 0;
                const assigned = s['assigned'] || 0;
                next[s['sectionId']] = { ...s, total, assigned, percentage: s['percentage'] ?? (total > 0 ? Math.round((assigned / total) * 100) : 0) };
                order.push(s['sectionId']);
            });
            setSectionProgress(next);
            setSectionOrder(order);
        };

        const applySnapshot = (payload) => {
            if (!payload || !isThisModule(payload)) return;
            applySections(payload['sections']);
            const status = payload['status'] || payload['phase'] || 'running';
            setJobSummary({
                moduleName: payload['moduleName'] || 'Module',
                total: payload['total'] || 0,
                totalBatches: payload['totalBatches'] || 0,
                updated: payload['updated'] || 0,
                status: status === 'complete' ? 'complete' : status === 'error' ? 'error' : 'running'
            });
            if (status === 'complete') { setIsPriorityLoading(false); setHasPrioritiesAlreadySet(true); }
            else if (status === 'error') setIsPriorityLoading(false);
            else setIsPriorityLoading(true);
        };

        const onStart = (payload) => {
            if (!isThisModule(payload)) return;
            toastedPriorityRef.current = false;
            applySections(payload['sections']);
            setJobSummary({ moduleName: payload['moduleName'], total: payload['total'] || 0, totalBatches: payload['totalBatches'], updated: 0, status: 'running' });
            setIsPriorityLoading(true);
        };
        const onSectionProgress = (payload) => {
            if (!payload || !isThisModule(payload)) return;
            const sid = payload['sectionId'];
            if (sid) {
                setSectionProgress(prev => ({ ...prev, [sid]: { sectionId: sid, sectionName: payload['sectionName'], objective: payload['objective'], tier: payload['tier'], total: payload['total'] || 0, assigned: payload['assigned'] || 0, percentage: payload['percentage'] || 0 } }));
                setSectionOrder(prev => prev.includes(sid) ? prev : [...prev, sid]);
            }
            setJobSummary(prev => ({
                ...(prev || {}),
                moduleName: payload['moduleName'] || prev?.['moduleName'] || 'Module',
                total: payload['jobTotal'] != null ? payload['jobTotal'] : (prev?.['total'] || 0),
                updated: payload['jobUpdated'] != null ? payload['jobUpdated'] : (prev?.['updated'] || 0),
                status: 'running'
            }));
            setIsPriorityLoading(true);
        };
        const onComplete = (payload) => {
            if (payload && !isThisModule(payload)) return;
            setJobSummary(prev => ({ ...(prev || {}), status: 'complete', updated: payload?.['updated'], total: payload?.['total'] != null ? payload['total'] : prev?.['total'] }));
            setIsPriorityLoading(false);
            setHasPrioritiesAlreadySet(true);
            if (!toastedPriorityRef.current) {
                toastedPriorityRef.current = true;
                showMessage(`Priorities set for ${payload?.['updated'] || 0} of ${payload?.['total'] || 0} questions`, 'success');
            }
        };
        const onError = (payload) => {
            if (payload && !isThisModule(payload)) return;
            setJobSummary(prev => ({ ...(prev || {}), status: 'error', errorMsg: payload?.['error'] }));
            setIsPriorityLoading(false);
            showMessage('Priority setting failed', 'error');
        };
        const requestStatus = () => socket.emit('priorityGeneration:getStatus', { userId: socketUserId || localStorage.getItem('userId') });
        const onConnect = () => { setIsSocketConnected(true); requestStatus(); };
        const onDisconnect = () => setIsSocketConnected(false);

        socket.on('priorityGeneration:start', onStart);
        socket.on('priorityGeneration:sectionProgress', onSectionProgress);
        socket.on('priorityGeneration:complete', onComplete);
        socket.on('priorityGeneration:error', onError);
        socket.on('priorityGeneration:currentStatus', applySnapshot);
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        if (socket.connected) requestStatus();
        return () => {
            socket.off('priorityGeneration:start', onStart);
            socket.off('priorityGeneration:sectionProgress', onSectionProgress);
            socket.off('priorityGeneration:complete', onComplete);
            socket.off('priorityGeneration:error', onError);
            socket.off('priorityGeneration:currentStatus', applySnapshot);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
        };
    }, []);

    const doSetPriorities = async () => {
        if (!moduleData?.['_id'] || isPriorityLoading) return;
        toastedPriorityRef.current = false;
        setSectionProgress({});
        setSectionOrder([]);
        setJobSummary({ moduleName: moduleData['moduleName'] || 'Module', total: 0, updated: 0, status: 'starting' });
        setIsPriorityLoading(true);
        try {
            await postRequest('/modules/set-question-priorities', { moduleId: moduleData['_id'] }, true);
            // isPriorityLoading cleared by priorityGeneration:complete / priorityGeneration:error socket events
        } catch {
            showMessage('Failed to start priority setting', 'error');
            setIsPriorityLoading(false);
        }
    };

    const handleSetPriorities = () => {
        if (!moduleData?.['_id'] || isPriorityLoading) return;
        if (hasPrioritiesAlreadySet) { dispatch(toggleModal({ open: 'priorityConfirmModal' })); return; }
        doSetPriorities();
    };

    const handleConfirmPriorityRegen = () => {
        dispatch(toggleModal({ open: '' }));
        doSetPriorities();
    };

    // ── AI generation mode (create-only) ──────────────────────────────────────
    // 'manual' = existing flow (sections entered by admin)
    // 'ai'     = GPT generates all sections/questions after module is saved
    const [generationMode, setGenerationMode] = useState('manual');
    const [aiConfig, setAiConfig] = useState({ sectionsPerCombo: 5, questionsPerSection: 10 });
    const [excelParsedData, setExcelParsedData] = useState(null);
    const [excelVariantModalOpen, setExcelVariantModalOpen] = useState(false);
    const [excelImportedVariants, setExcelImportedVariants] = useState([]);

    const currentVariantKey = getVariantKey(selectedObjective, selectedTier);

    // Investment Readiness modules (moduleKey === 'EHE') skip sections/objectives entirely
    // and render the 25 EHE questions directly for inline editing.
    const isInvestmentReadiness = isEdit && moduleData?.slug === 'Investment Readiness';
    const irQuestionsRef = useRef(null);

    // Exit Readiness module skips sections/objectives and renders questions from the
    // production Module + Section + Question tables directly for inline editing.
    const isExitReadiness = isEdit && moduleData?.moduleName === 'Exit Readiness';

    // Investor Lead module (slug === 'investor-lead') renders the DEPF008 section
    // and its questions from the production Section + Question tables directly.
    const isInvestorLead = isEdit && moduleData?.slug === 'investor-lead';

    // Clone targets include all objective+tier variants except current source variant.
    const getCloneTargetOptions = () => {
        const options = [];
        COMPANY_OBJECTIVES.forEach((objective) => {
            SUBSCRIPTION_TIERS.forEach((tier) => {
                const key = getVariantKey(objective, tier);
                if (key !== currentVariantKey) {
                    options.push({ key, objective, tier, label: `${objective} → ${tier}` });
                }
            });
        });
        if (currentVariantKey !== getVariantKey(OBJECTIVE_ALL, TIER_ALL)) {
            options.push({
                key: getVariantKey(OBJECTIVE_ALL, TIER_ALL),
                objective: OBJECTIVE_ALL,
                tier: TIER_ALL,
                label: `${OBJECTIVE_ALL} → ${TIER_ALL}`
            });
        }
        return options;
    };
    const cloneTargetOptions = getCloneTargetOptions();
    // Fall back to {objective}-All then All-All when the exact variant has no sections.
    // BA and similar modules store sections with subscriptionTier='All', so their keys
    // are Investment-All / Growth-All / Exit-All rather than the 9 standard combos.
    const effectiveVariantKey =
        sectionsByVariant[currentVariantKey]?.length ? currentVariantKey :
            sectionsByVariant[getVariantKey(selectedObjective, TIER_ALL)]?.length ? getVariantKey(selectedObjective, TIER_ALL) :
                sectionsByVariant[getVariantKey(OBJECTIVE_ALL, TIER_ALL)]?.length ? getVariantKey(OBJECTIVE_ALL, TIER_ALL) :
                    currentVariantKey;
    const currentSections = sectionsByVariant[effectiveVariantKey] || [];


    const defaultValues = {
        moduleName: '',
        moduleKey: '',
        slug: '',
        moduleSubTitle: '',
        moduleTagColor: '',
        moduleDescription: '',
        imageURL: '',
        type: 'common',
        dashboardCardId: '',
        isActive: true,
        isReportModule: false,
        isCustomSection: false,
        isPaid: false,
        price: '',
        menusConfig: [],
        duration: 'NIL',
        completeness: 'No',
        risks: 'No',
        passFail: 'No',
        certification: 'No',
        report: 'No',
        workflowReport: 'No'
    };

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(moduleFormSchema),
        mode: 'onChange',
        defaultValues: moduleData && isEdit ? {
            moduleName: moduleData.moduleName || '',
            moduleKey: moduleData.moduleKey || '',
            slug: (moduleData.slug || '').replace(/^#/, ''),
            moduleSubTitle: moduleData.moduleSubTitle || '',
            moduleTagColor: moduleData.moduleTagColor || '',
            moduleDescription: moduleData.moduleDescription || '',
            imageURL: moduleData.imageURL || '',
            type: moduleData.type || 'common',
            dashboardCardId: moduleData.dashboardCardId ? String(moduleData.dashboardCardId) : '',
            isActive: moduleData.isActive !== undefined ? moduleData.isActive : true,
            isReportModule: moduleData.isReportModule !== undefined ? moduleData.isReportModule : false,
            isCustomSection: moduleData.isCustomSection !== undefined ? moduleData.isCustomSection : false,
            isDirectorsModule: moduleData.isDirectorsModule !== undefined ? moduleData.isDirectorsModule : false,
            isPaid: moduleData.isPaid || false,
            price: moduleData.price || '',
            menusConfig: moduleData.menusConfig || [],
            duration: moduleData.duration || 'NIL',
            completeness: moduleData.completeness || 'No',
            risks: moduleData.risks || 'No',
            passFail: moduleData.passFail || 'No',
            certification: moduleData.certification || 'No',
            report: moduleData.report || 'No',
            workflowReport: moduleData.workflowReport || 'No'
        } : defaultValues
    });

    console.log(errors);

    // Watch all form values for preview
    const watchedValues = watch();
    const moduleName = watch('moduleName');

    // ------------------------------------------------------------
    // Dynamic module menus (How it works?, Help, What is it?, etc.)
    // ------------------------------------------------------------
    const [menusConfig, setMenusConfig] = useState(
        (moduleData && isEdit && Array.isArray(moduleData.menusConfig)) ? moduleData.menusConfig : []
    );
    const [expandedMenuKey, setExpandedMenuKey] = useState(null);

    // Keep RHF value in sync with local state (one-way) to avoid loops
    useEffect(() => {
        setValue('menusConfig', menusConfig);
    }, [menusConfig, setValue]);

    useEffect(() => {
        return () => {
            deleteUnlockersRef.current.forEach((unlocker) => {
                if (unlocker && typeof unlocker.cancel === 'function') unlocker.cancel();
            });
            if (
                debouncedFlushSectionDeletesRef.current
                && typeof debouncedFlushSectionDeletesRef.current.cancel === 'function'
            ) {
                debouncedFlushSectionDeletesRef.current.cancel();
            }
        };
    }, []);

    const flushQueuedSectionDeletes = async () => {
        const queuedItems = [...sectionDeleteQueueRef.current.values()];
        if (!queuedItems.length) return;

        sectionDeleteQueueRef.current.clear();

        const persistedSectionIds = [...new Set(queuedItems.map((item) => item.sectionId).filter(Boolean))];
        let successfullyDeletedIdSet = new Set();
        if (persistedSectionIds.length > 0) {
            const response = await deleteRequest('/modules/temp-sections/bulk', { sectionIds: persistedSectionIds });
            if (response?.code !== 200) {
                setPendingSectionDeleteKeys(new Set());
                return;
            }
            const deletedSectionIds = response?.body?.deletedSectionIds || [];
            successfullyDeletedIdSet = new Set((deletedSectionIds || []).map((id) => String(id)));
        }

        setSectionsByVariant((prev) => {
            const next = { ...prev };
            const pendingUnsavedKeySet = new Set(
                queuedItems.filter((item) => !item.sectionId).map((item) => item.queueKey)
            );
            Object.keys(next).forEach((variantKey) => {
                next[variantKey] = (next[variantKey] || []).filter((section) => {
                    const sectionId = section?._id ? String(section._id) : null;
                    if (sectionId) {
                        // Hide only backend-confirmed deletions for persisted sections.
                        return !successfullyDeletedIdSet.has(sectionId);
                    }
                    const sectionQueueKey = `variant:${variantKey}:sectionNo:${String(section?.sectionNo || '').trim()}`;
                    // Unsaved queued sections are local-only and can be removed immediately after debounce flush.
                    return !pendingUnsavedKeySet.has(sectionQueueKey);
                });
            });
            return next;
        });

        setPendingSectionDeleteKeys(new Set());
    };

    const debouncedFlushSectionDeletesRef = useRef(null);
    if (!debouncedFlushSectionDeletesRef.current) {
        debouncedFlushSectionDeletesRef.current = debounce(() => {
            flushQueuedSectionDeletes();
        }, DELETE_DEBOUNCE_MS);
    }

    const createEmptyMenu = () => {
        const idx = (menusConfig || []).length + 1;
        return {
            key: `menu-${idx}`,
            label: '',
            iconURL: '',
            isActive: true,
            actionType: 'navigate',
            navigateUrl: '',
            modalTitle: '',
            modalImageURL: '',
            modalContent: '',
            modalImagePosition: 'right'
        };
    };

    const handleAddMenu = () => {
        const next = [...(menusConfig || []), createEmptyMenu()];
        setMenusConfig(next);
        if (next.length > 0) setExpandedMenuKey(next[next.length - 1].key);
    };

    const handleRemoveMenu = (key) => {
        setMenusConfig((prev) => (prev || []).filter((m) => m.key !== key));
        if (expandedMenuKey === key) setExpandedMenuKey(null);
    };

    const handleMenuChange = (key, field, value) => {
        setMenusConfig((prev) =>
            (prev || []).map((m) =>
                m.key === key
                    ? {
                        ...m,
                        [field]: value
                    }
                    : m
            )
        );
    };

    // Generate module key from module name
    const generateModuleKey = (name) => {
        if (!name || !name.trim()) return '';

        // Extract first letter of each word and make uppercase
        const words = name.trim().split(/\s+/);
        const initials = words
            .map(word => word.charAt(0).toUpperCase())
            .join('');

        return initials;
    };

    // Auto-generate module key when module name changes (only for new modules and if not manually edited)
    useEffect(() => {
        if (moduleName && !isEdit && !isModuleKeyManuallyEdited) {
            const generatedKey = generateModuleKey(moduleName);
            setValue('moduleKey', generatedKey);
        }
    }, [moduleName, isEdit, isModuleKeyManuallyEdited, setValue]);

    // Handle module key input - only allow alphanumeric characters and capitalize letters
    const handleModuleKeyChange = (e) => {
        const value = e.target.value;
        // Remove non-alphanumeric characters and capitalize letters
        const sanitizedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        setValue('moduleKey', sanitizedValue);

        // Mark as manually edited if user types something
        if (!isModuleKeyManuallyEdited) {
            setIsModuleKeyManuallyEdited(true);
        }
    };

    // Auto-update all section and question numbers when module key changes (per variant)
    useEffect(() => {
        const moduleKey = watchedValues.moduleKey;
        if (!moduleKey) return;

        const updateSectionNumbers = (sectionsList) => {
            return sectionsList.map((section, idx) => {
                const sectionNum = idx + 1;
                const paddedSectionNum = String(sectionNum).padStart(3, '0');
                const sectionNo = `${moduleKey}${paddedSectionNum}`;
                const updatedQuestion = section.question ? {
                    ...section.question,
                    qNo: sectionNo,
                    questionNumber: sectionNum
                } : section.question;
                let updatedSubFolders = {};
                if (section.subFolders && typeof section.subFolders === 'object') {
                    Object.keys(section.subFolders).forEach((key) => {
                        const subFolder = section.subFolders[key];
                        const subFolderNo = `${sectionNo}.${key}`;
                        updatedSubFolders[key] = {
                            ...subFolder,
                            no: subFolderNo,
                            question: subFolder.question ? { ...subFolder.question, qNo: subFolderNo } : subFolder.question
                        };
                    });
                }
                return { ...section, sectionNo, question: updatedQuestion, subFolders: updatedSubFolders };
            });
        };

        let hasChange = false;
        const updated = { ...sectionsByVariant };
        Object.keys(updated).forEach(variantKey => {
            const list = updated[variantKey] || [];
            if (list.length === 0) return;
            const next = updateSectionNumbers(list);
            if (JSON.stringify(next) !== JSON.stringify(list)) {
                updated[variantKey] = next;
                hasChange = true;
            }
        });
        if (hasChange) {
            setSectionsByVariant(updated);
            // All section numbers changed — every section needs to be saved
            if (isEdit) allSectionsDirtyRef.current = true;
        }
    }, [watchedValues.moduleKey]);


    // Update sections when moduleData changes (edit mode) – group by companyObjective and subscriptionTier
    useEffect(() => {
        if (moduleData?.sections && isEdit) {
            const buildSubFoldersFromSubSections = (subSections) => {
                if (!Array.isArray(subSections)) return {};
                const result = {};
                subSections.forEach((sf, index) => {
                    const key = String(index + 1);
                    result[key] = {
                        name: sf.name || '',
                        no: sf.no || '',
                        question: sf.question || {},
                        subFolders: buildSubFoldersFromSubSections(sf.subSections || [])
                    };
                });
                return result;
            };

            const transformedSections = moduleData.sections.map((section, idx) => {
                const sectionNo = section.sectionNo || `${moduleData.moduleKey}${(idx + 1).toString().padStart(3, '0')}`;
                let question = section.question || {
                    qNo: sectionNo,
                    questionNumber: idx + 1,
                    qDescription: '',
                    questionType: '',
                    customOptions: [{ value: '', info: '' }],
                    predefinedOptionsInfo: {},
                    dateInfo: '',
                    tooltip: '',
                    toolboxOffer: '',
                    riskDescription: '',
                    recommendDescription: '',
                    riskScores: {}
                };
                let subFolders = {};
                if (Array.isArray(section.subSections)) {
                    subFolders = buildSubFoldersFromSubSections(section.subSections);
                } else if (section.subFolders && typeof section.subFolders === 'object') {
                    subFolders = section.subFolders;
                }
                return {
                    ...section,
                    sectionNo,
                    question,
                    subFolders,
                    companyObjective: section.companyObjective || 'All',
                    subscriptionTier: section.subscriptionTier || 'All'
                };
            });

            const groupedSingle = emptySectionsByVariant();
            transformedSections.forEach(sec => {
                const obj = sec.companyObjective === 'All' ? OBJECTIVE_ALL : sec.companyObjective;
                const tier = sec.subscriptionTier === 'All' ? TIER_ALL : sec.subscriptionTier;
                const key = getVariantKey(obj, tier);
                if (!groupedSingle[key]) groupedSingle[key] = [];
                groupedSingle[key].push(sec);
            });
            setSectionsByVariant(groupedSingle);
            if (transformedSections.length > 0) setExpandedSectionIndex(0);
        }
    }, [moduleData, isEdit]);

    // Flatten sections from all variants for parent and submit
    const flattenedSections = () => {
        const out = [];
        Object.keys(sectionsByVariant).forEach(variantKey => {
            const [objective, tier] = variantKey.split('-');
            (sectionsByVariant[variantKey] || []).forEach(sec => {
                out.push({
                    ...sec,
                    companyObjective: sec.companyObjective ?? objective,
                    subscriptionTier: sec.subscriptionTier ?? tier
                });
            });
        });
        return out;
    };

    // Update parent component when form changes
    useEffect(() => {
        if (onFormChange) {
            onFormChange({
                ...watchedValues,
                sections: flattenedSections(),
                dashboardPlacements: Object.keys(placementsByVariant).reduce((acc, key) => {
                    const cardId = placementsByVariant[key];
                    if (!cardId) return acc;
                    const [objective, tier] = key.split('-');
                    acc.push({
                        companyObjective: objective,
                        subscriptionTier: tier,
                        dashboardCardId: cardId
                    });
                    return acc;
                }, []),
                sectionAccessRestrictions: Object.keys(sectionRestrictionsByVariant).map((key) => {
                    const parsed = parseVariantKey(key);
                    if (!parsed) return null;
                    return {
                        companyObjective: parsed.objective,
                        subscriptionTier: parsed.tier,
                        isRestricted: sectionRestrictionsByVariant[key] === true
                    };
                }).filter(Boolean)
            });
        }
    }, [watchedValues, sectionsByVariant, placementsByVariant, sectionRestrictionsByVariant, onFormChange]);

    // Reset form when moduleData changes in edit mode
    useEffect(() => {
        if (moduleData && isEdit) {
            reset({
                moduleName: moduleData.moduleName || '',
                moduleKey: moduleData.moduleKey || '',
                slug: (moduleData.slug || '').replace(/^#/, ''),
                moduleSubTitle: moduleData.moduleSubTitle || '',
                moduleTagColor: moduleData.moduleTagColor || '',
                moduleDescription: moduleData.moduleDescription || '',
                imageURL: moduleData.imageURL || '',
                type: moduleData.type || 'common',
                dashboardCardId: moduleData.dashboardCardId ? String(moduleData.dashboardCardId) : '',
                isActive: moduleData.isActive !== undefined ? moduleData.isActive : true,
                isReportModule: moduleData.isReportModule !== undefined ? moduleData.isReportModule : false,
                isDirectorsModule: moduleData.isDirectorsModule !== undefined ? moduleData.isDirectorsModule : false,
                isPaid: moduleData.isPaid || false,
                price: moduleData.price || '',
                stripeProductPriceId: moduleData.stripeProductPriceId || '',
                duration: moduleData.duration || 'NIL',
                completeness: moduleData.completeness || 'No',
                risks: moduleData.risks || 'No',
                passFail: moduleData.passFail || 'No',
                certification: moduleData.certification || 'No',
                report: moduleData.report || 'No',
                workflowReport: moduleData.workflowReport || 'No'
            });

            // Prime certificationRules
            setCertificationRules(Array.isArray(moduleData.certificationRules) ? moduleData.certificationRules : []);

            // Prime placements from moduleData.dashboardPlacements if present
            if (Array.isArray(moduleData.dashboardPlacements)) {
                const initialPlacements = {};
                moduleData.dashboardPlacements.forEach((p) => {
                    if (!p || !p.dashboardCardId) return;
                    const obj = p.companyObjective || OBJECTIVE_ALL;
                    const tier = p.subscriptionTier || TIER_ALL;
                    const key = getVariantKey(obj, tier);
                    initialPlacements[key] = String(p.dashboardCardId);
                });
                setPlacementsByVariant(initialPlacements);
            }
            if (Array.isArray(moduleData.sectionAccessRestrictions)) {
                const next = emptySectionRestrictions();
                moduleData.sectionAccessRestrictions.forEach((r) => {
                    if (!r) return;
                    const obj = r.companyObjective != null ? r.companyObjective : OBJECTIVE_ALL;
                    const tier = r.subscriptionTier != null ? r.subscriptionTier : TIER_ALL;
                    const key = getVariantKey(obj, tier);
                    if (Object.prototype.hasOwnProperty.call(next, key)) {
                        next[key] = r.isRestricted === true;
                    }
                });
                setSectionRestrictionsByVariant(next);
            } else {
                setSectionRestrictionsByVariant(emptySectionRestrictions());
            }
        }
    }, [moduleData, isEdit, reset]);

    // Helper function to validate a single question
    // NOTE: Questions and their options are optional. If a question is left empty,
    // we skip validation entirely. If the user starts filling a question, we only
    // require description and type – options are not mandatory.
    const validateQuestion = (question) => {
        const errors = {};
        let isValid = true;

        if (!question) {
            return { errors, isValid: true }; // No question is okay
        }

        const hasCustomOptions = question.customOptions &&
            question.customOptions.length > 0 &&
            question.customOptions.some(opt => customOptionPrimaryLabel(opt));

        const hasPredefinedOptions = question.predefinedOptionsInfo &&
            Object.keys(question.predefinedOptionsInfo).length > 0 &&
            Object.values(question.predefinedOptionsInfo).some(val => val?.trim && val.trim());

        // If the whole question is effectively empty, allow it
        const isEmptyQuestion = !question.qDescription?.trim() &&
            !question.questionType &&
            !hasCustomOptions &&
            !hasPredefinedOptions &&
            !question.dateInfo?.trim() &&
            !question.tooltip?.trim() &&
            !question.toolboxOffer?.trim() &&
            !question.riskDescription?.trim() &&
            !question.recommendDescription?.trim();

        if (isEmptyQuestion) {
            return { errors, isValid: true };
        }

        // If the author started a question, require at least description and type
        if (!question.qDescription?.trim()) {
            errors.qDescription = 'Question is required';
            isValid = false;
        }

        if (!question.questionType) {
            errors.questionType = 'Question type is required';
            isValid = false;
        }

        // Options (customOptions / predefinedOptionsInfo) are allowed to be empty now
        return { errors, isValid };
    };

    // Helper function to validate sub-folders (object with numbered keys)
    // NOTE: Sub-folder name is optional. We only validate nested questions, not the label.
    const validateSubFolders = (subFolders, parentPath = 'Sub-Folder') => {
        const errors = {};
        let isValid = true;

        if (subFolders && typeof subFolders === 'object') {
            Object.keys(subFolders).forEach((key) => {
                const subFolder = subFolders[key];
                errors[key] = {};

                // Validate question in sub-folder
                const questionValidation = validateQuestion(subFolder.question);
                if (Object.keys(questionValidation.errors).length > 0) {
                    errors[key].question = questionValidation.errors;
                }
                if (!questionValidation.isValid) {
                    isValid = false;
                }
            });
        }

        return { errors, isValid };
    };

    const validateSections = () => {
        const allSections = flattenedSections();
        console.log('  - Total sections:', allSections.length);

        const errors = {};
        let isValid = true;

        Object.keys(sectionsByVariant).forEach(variantKey => {
            const list = sectionsByVariant[variantKey] || [];
            list.forEach((section, sIdx) => {
                const errKey = `${variantKey}-${sIdx}`;
                errors[errKey] = {};

                // Section name is now optional – no validation here

                // Validate main question using relaxed rules (optional when empty)
                const questionValidation = validateQuestion(section.question);
                if (Object.keys(questionValidation.errors).length > 0) {
                    errors[errKey].question = questionValidation.errors;
                }
                if (!questionValidation.isValid) isValid = false;

                // Validate sub-folder questions (names optional)
                const hasSubFolders = section.subFolders && Object.keys(section.subFolders).length > 0;
                if (hasSubFolders) {
                    const subFolderValidation = validateSubFolders(section.subFolders, `Section ${sIdx + 1}`);
                    if (Object.keys(subFolderValidation.errors).length > 0) {
                        errors[errKey].subFolders = subFolderValidation.errors;
                    }
                    if (!subFolderValidation.isValid) isValid = false;
                }
            });
        });

        setSectionErrors(errors);
        return isValid;
    };

    // Helper function to clean sections data
    const cleanSectionsData = (sections) => {
        return sections.map(section => {
            // Return section as is - subFolders are already in correct format
            return {
                ...section
            };
        });
    };

    /**
     * Convert parsed Excel dataset into sectionsByVariant and merge into state.
     * Called after the user confirms their objective×tier selection in ExcelVariantModal.
     *
     * @param {string[]} selectedVariants — e.g. ['Investment-Free', 'Growth-Pro']
     * @param {object}   parsedData       — body returned by /modules/parse-excel
     */
    const handleExcelImport = (selectedVariants, parsedData) => {
        const currentModuleKey = (watchedValues.moduleKey || '').toUpperCase();
        if (!currentModuleKey) {
            showMessage('Please set the Module Key before importing from Excel.');
            return;
        }

        const buildSectionsForVariant = (variantKey) => {
            const [objective, tier] = variantKey.split('-');
            return (parsedData['sections'] || []).map((sec, sectionIdx) => {
                const sectionNum = String(sectionIdx + 1).padStart(3, '0');
                const sectionNo = `${currentModuleKey}${sectionNum}`;

                const subFolders = {};
                let sfCounter = 0;
                (sec['questions'] || []).forEach((q) => {
                    const qDescription = (q['question'] || '').trim();
                    if (!qDescription) return; // skip questions with no content
                    sfCounter++;
                    const sfKey = String(sfCounter);
                    const qNo = `${sectionNo}.${sfKey}`;

                    // Build riskScores from imported per-answer risk values
                    const riskScores = {};
                    if (q['riskIfYes'] !== null && q['riskIfYes'] !== undefined) riskScores['Yes'] = q['riskIfYes'];
                    if (q['riskIfNo'] !== null && q['riskIfNo'] !== undefined) riskScores['No'] = q['riskIfNo'];

                    // Build predefinedOptionsInfo from imported ifYes / ifNo
                    const predefinedOptionsInfo = {};
                    if (q['ifYes']) predefinedOptionsInfo['Yes'] = q['ifYes'];
                    if (q['ifNo']) predefinedOptionsInfo['No'] = q['ifNo'];

                    // Build customOptions for Multi/DropDown types
                    const customOptions = Array.isArray(q['options']) && q['options'].length > 0
                        ? q['options'].map(o => ({ value: o, info: '' }))
                        : [{ value: '', info: '' }];

                    subFolders[sfKey] = {
                        name: q['subsection'] || `${sectionNo}.${sfKey}`,
                        no: qNo,
                        question: {
                            qNo,
                            questionNumber: sfCounter,
                            qDescription,
                            riskDescription: q['risk'] || '',
                            recommendDescription: q['recommendation'] || '',
                            tooltip: q['tooltip'] || '',
                            toolboxOffer: q['toolbox'] || '',
                            questionType: q['questionType'] || 'YN/F/T',
                            riskScore: q['riskScore'] ?? 1,
                            attachmentRequired: q['attachmentRequired'] ?? false,
                            customOptions,
                            predefinedOptionsInfo,
                            dateInfo: '',
                            riskScores,
                        },
                        subFolders: {}
                    };
                });

                return {
                    sectionName: sec['sectionName'],
                    sectionNo,
                    companyObjective: objective,
                    subscriptionTier: tier,
                    subFolders
                };
            });
        };

        setSectionsByVariant(prev => {
            const next = { ...prev };
            for (const vk of selectedVariants) {
                next[vk] = buildSectionsForVariant(vk);
            }
            return next;
        });

        allSectionsDirtyRef.current = true;
        setExcelImportedVariants(selectedVariants);
        setExcelVariantModalOpen(false);
        // Stay in 'upload' mode — sections are loaded into state and will be submitted on Save.
    };

    const onSubmitForm = async (data) => {
        // AI mode (create-only): skip section validation — GPT will generate content after save
        if (generationMode === 'ai' && !isEdit) {
            if (onSubmit) {
                onSubmit({
                    ...data,
                    sections: [],
                    changedSectionsOnly: false,
                    dashboardPlacements: [],
                    sectionAccessRestrictions: [],
                    certificationRules,
                    generationMode: 'ai',
                    aiConfig,
                });
            }
            return;
        }

        const sectionsValid = validateSections();
        if (!sectionsValid) {
            window.scrollTo({ top: document.querySelector('.section-container')?.offsetTop || 0, behavior: 'smooth' });
            return;
        }
        const allSections = flattenedSections();
        const cleanedSections = cleanSectionsData(allSections);

        // In edit mode, only send sections that were actually modified to avoid
        // redundant DB writes for every section in large modules.
        // New sections (no _id) are always included.
        // changedSectionsOnly tells the backend to skip its deletion pass since
        // section deletes are already handled in real-time via /temp-sections/bulk.
        const changedSectionsOnly = isEdit && !allSectionsDirtyRef.current;
        const sectionsToSubmit = changedSectionsOnly
            ? cleanedSections.filter(s => !s._id || dirtySectionIdsRef.current.has(String(s._id)))
            : cleanedSections;

        const dashboardPlacements = Object.keys(placementsByVariant).reduce((acc, key) => {
            const cardId = placementsByVariant[key];
            if (!cardId) return acc;
            const [objective, tier] = key.split('-');
            acc.push({
                companyObjective: objective,
                subscriptionTier: tier,
                dashboardCardId: cardId
            });
            return acc;
        }, []);
        const sectionAccessRestrictions = Object.keys(sectionRestrictionsByVariant).map((key) => {
            const parsed = parseVariantKey(key);
            if (!parsed) return null;
            return {
                companyObjective: parsed.objective,
                subscriptionTier: parsed.tier,
                isRestricted: sectionRestrictionsByVariant[key] === true
            };
        }).filter(Boolean);
        // For IR modules, save all questions before updating module metadata
        if (isInvestmentReadiness && irQuestionsRef.current) {
            await irQuestionsRef.current.saveAll();
        }

        if (onSubmit) {
            onSubmit({
                ...data,
                sections: sectionsToSubmit,
                changedSectionsOnly,
                dashboardPlacements,
                sectionAccessRestrictions,
                certificationRules
            });
        } else {
            console.error('⚠️ onSubmit callback missing!');
        }
    };

    const addSection = () => {
        const sectionNumber = currentSections.length + 1;
        const paddedSectionNumber = String(sectionNumber).padStart(3, '0');
        const sectionNo = `${watchedValues.moduleKey}${paddedSectionNumber}`;
        const newSection = {
            sectionName: '',
            sectionNo: sectionNo,
            companyObjective: selectedObjective,
            subscriptionTier: selectedTier,
            question: {
                qNo: sectionNo,
                questionNumber: sectionNumber,
                qDescription: '',
                questionType: '',
                customOptions: [{ value: '', info: '' }],
                predefinedOptionsInfo: {},
                dateInfo: '',
                tooltip: '',
                toolboxOffer: '',
                riskDescription: '',
                recommendDescription: '',
                riskScores: {}
            },
            subFolders: {}
        };
        setSectionsByVariant(prev => ({
            ...prev,
            [currentVariantKey]: [...(prev[currentVariantKey] || []), newSection]
        }));
        setExpandedSectionIndex(currentSections.length);
    };

    const updateSection = (variantKey, index, updatedSection) => {
        // Mark this section as dirty so we only save changed sections on submit
        if (updatedSection._id) {
            dirtySectionIdsRef.current.add(String(updatedSection._id));
        }
        setSectionsByVariant(prev => {
            const list = [...(prev[variantKey] || [])];
            list[index] = { ...updatedSection, companyObjective: updatedSection.companyObjective ?? selectedObjective, subscriptionTier: updatedSection.subscriptionTier ?? selectedTier };
            return { ...prev, [variantKey]: list };
        });
    };

    const acquireDeleteLock = (key) => {
        if (deleteLocksRef.current.get(key)) {
            return true;
        }
        deleteLocksRef.current.set(key, true);

        let unlocker = deleteUnlockersRef.current.get(key);
        if (!unlocker) {
            unlocker = debounce((lockKey) => {
                deleteLocksRef.current.delete(lockKey);
            }, DELETE_DEBOUNCE_MS);
            deleteUnlockersRef.current.set(key, unlocker);
        }
        unlocker(key);
        return false;
    };

    const removeSection = async (variantKey, index) => {
        const sectionToDelete = (sectionsByVariant[variantKey] || [])[index];
        if (!sectionToDelete) return;

        const sectionId = sectionToDelete?._id;
        const queueKey = sectionId
            ? `id:${String(sectionId)}`
            : `variant:${variantKey}:sectionNo:${String(sectionToDelete?.sectionNo || '').trim()}`;

        if (sectionDeleteQueueRef.current.has(queueKey)) return;
        sectionDeleteQueueRef.current.set(queueKey, { queueKey, sectionId: sectionId ? String(sectionId) : null, variantKey });
        setPendingSectionDeleteKeys((prev) => {
            const next = new Set(prev);
            next.add(queueKey);
            return next;
        });
        showMessage('Delete queued. Will execute in 3 seconds.');
        debouncedFlushSectionDeletesRef.current();
    };

    // Deep clone section with new sectionNo and qNo for target objective+tier
    const cloneSectionForTarget = (section, targetObjective, targetTier, startSectionNo) => {
        const newSectionNo = startSectionNo;
        const updateQNoInQuestion = (q, baseNo) => {
            if (!q) return q;
            return { ...q, qNo: baseNo, _id: undefined };
        };
        const updateSubFolders = (subFolders, parentNo) => {
            if (!subFolders || typeof subFolders !== 'object') return {};
            const result = {};
            Object.keys(subFolders).forEach((key) => {
                const sf = subFolders[key];
                const subNo = `${parentNo}.${key}`;
                result[key] = {
                    ...sf,
                    no: subNo,
                    question: sf.question ? updateQNoInQuestion(sf.question, subNo) : sf.question,
                    subFolders: updateSubFolders(sf.subFolders || {}, subNo)
                };
            });
            return result;
        };
        const newQuestion = section.question
            ? updateQNoInQuestion(section.question, newSectionNo)
            : section.question;
        const newSubFolders = updateSubFolders(section.subFolders || {}, newSectionNo);
        return {
            ...section,
            _id: undefined,
            sectionNo: newSectionNo,
            companyObjective: targetObjective,
            subscriptionTier: targetTier,
            question: newQuestion,
            subFolders: newSubFolders
        };
    };

    const openBulkCloneModal = () => {
        if (currentSections.length === 0) return;
        setCloneMode('bulk');
        setCloneSection(null);
        setCloneTargets([]);
        setCloneView('select');
        setCloneConflictConflicts([]);
        setCloneModalOpen(true);
    };

    const openSingleCloneModal = (section) => {
        setCloneMode('single');
        setCloneSection(section);
        setCloneTargets([]);
        setCloneView('select');
        setCloneConflictConflicts([]);
        setCloneModalOpen(true);
    };

    // Detect conflicts: sections with same sectionNo already exist in target tier(s)
    const getCloneConflicts = () => {
        const sectionsToClone = cloneMode === 'bulk' ? currentSections : (cloneSection ? [cloneSection] : []);
        const conflicts = [];
        cloneTargets.forEach((targetKey) => {
            const [targetObjective, targetTier] = targetKey.split('-');
            const existing = sectionsByVariant[targetKey] || [];
            const existingSectionNos = new Set(existing.map((s) => s.sectionNo).filter(Boolean));
            sectionsToClone.forEach((sec) => {
                const sectionNo = sec.sectionNo;
                if (sectionNo && existingSectionNos.has(sectionNo)) {
                    conflicts.push({ sectionNo, objective: targetObjective, tier: targetTier });
                }
            });
        });
        return conflicts;
    };

    const handleCloneConfirm = (forceOverwrite = false) => {
        if (cloneTargets.length === 0) return;
        const sectionsToClone = cloneMode === 'bulk' ? currentSections : (cloneSection ? [cloneSection] : []);
        if (sectionsToClone.length === 0) {
            setCloneModalOpen(false);
            return;
        }
        const conflicts = getCloneConflicts();
        if (conflicts.length > 0 && !forceOverwrite) {
            setCloneConflictConflicts(conflicts);
            setCloneView('conflict');
            return;
        }
        const moduleKey = watchedValues.moduleKey || 'XX';
        setSectionsByVariant(prev => {
            const next = { ...prev };
            cloneTargets.forEach((targetKey) => {
                const [targetObjective, targetTier] = targetKey.split('-');
                const existing = next[targetKey] || [];
                const cloned = sectionsToClone.map((sec) => {
                    const sectionNo = sec.sectionNo || `${moduleKey}${String(existing.length + 1).padStart(3, '0')}`;
                    return cloneSectionForTarget(sec, targetObjective, targetTier, sectionNo);
                });
                if (forceOverwrite && conflicts.length > 0) {
                    const clonedByNo = Object.fromEntries(cloned.map((c) => [c.sectionNo, c]));
                    const existingSectionNos = new Set(existing.map((s) => s.sectionNo).filter(Boolean));
                    const replaced = existing.map((s) => clonedByNo[s.sectionNo] ?? s);
                    const added = cloned.filter((c) => !existingSectionNos.has(c.sectionNo));
                    next[targetKey] = [...replaced, ...added];
                } else {
                    next[targetKey] = [...existing, ...cloned];
                }
            });
            return next;
        });
        setCloneModalOpen(false);
        setCloneTargets([]);
        setCloneSection(null);
        setCloneView('select');
        setCloneConflictConflicts([]);
    };

    const handleCloneOverwrite = () => {
        handleCloneConfirm(true);
    };

    const handleCloneCancel = () => {
        setCloneModalOpen(false);
        setCloneTargets([]);
        setCloneSection(null);
        setCloneView('select');
        setCloneConflictConflicts([]);
    };

    const toggleCloneTarget = (targetKey) => {
        setCloneTargets(prev =>
            prev.includes(targetKey) ? prev.filter((t) => t !== targetKey) : [...prev, targetKey]
        );
    };

    return (
        <div className="w100" style={{ minWidth: 0, width: '100%' }}>
            <div className="bg1 shadow br10px p2rem" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <div className="df aic jcsb mb1-5rem" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div className="df aic" style={{ gap: '0.75rem' }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="df aic jcc cp bg3 c1"
                            style={{
                                width: '42px',
                                height: '42px',
                                borderRadius: '50%',
                                border: '1px solid #d6ddff',
                                boxShadow: '0 4px 12px rgba(76, 110, 245, 0.16)',
                                flexShrink: 0,
                                transition: 'all 0.22s ease',
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 19l-7-7 7-7" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <h2 className="fs1-5rem fw600 c15">
                            {isEdit ? 'Edit Module' : 'Create New Module'}
                        </h2>
                    </div>
                    {isEdit && !moduleName?.includes('KPI') && (
                        <button
                            type="button"
                            onClick={handleSetPriorities}
                            disabled={isPriorityLoading}
                            className="df aic br50px py0-5rem px1-5rem fs0-875rem fw500 cp"
                            style={{ backgroundColor: isPriorityLoading ? '#D9D9D9' : '#4A67FF', color: '#fff', border: 'none', opacity: isPriorityLoading ? 0.7 : 1, gap: '6px' }}
                        >
                            <span style={{ fontSize: '14px' }}>✦</span>
                            {isPriorityLoading ? 'Setting Priorities…' : 'Set Priorities as per Questions'}
                        </button>
                    )}
                </div>
                {jobSummary && (() => {
                    const totalAssigned = jobSummary['updated'] != null ? jobSummary['updated'] : Object.values(sectionProgress).reduce((s, sec) => s + (sec['assigned'] || 0), 0);
                    const overallPct = jobSummary['total'] > 0 ? Math.round((totalAssigned / jobSummary['total']) * 100) : 0;
                    const isDone = jobSummary['status'] === 'complete';
                    const isStarting = jobSummary['status'] === 'starting';
                    return (
                        <div className="br10px bss bw1px mb1rem oh" style={{ borderColor: '#DDE2F2', background: '#fff' }}>
                            {/* Header */}
                            <div className="df jcsb aic px1-5rem py0-75rem" style={{ borderBottom: '1px solid #DDE2F2', background: '#F0F2FD' }}>
                                <div className="df aic" style={{ gap: '10px' }}>
                                    {isPriorityLoading && <span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', background: '#4A67FF', animation: 'pulse 1.2s ease-in-out infinite', flexShrink: 0 }} />}
                                    {isDone && <span className="fs14px" style={{ color: '#09B255' }}>✓</span>}
                                    <span className="fs14px fw600" style={{ color: isDone ? '#09B255' : '#4A67FF' }}>{jobSummary['moduleName']} — Priority Analysis</span>
                                    {isPriorityLoading && !isSocketConnected && <span className="fs12px fw500" style={{ color: '#FC7785' }}>Reconnecting…</span>}
                                </div>
                                <div className="df aic" style={{ gap: '12px' }}>
                                    <span className="fs13px fw500" style={{ color: '#505050' }}>{isStarting ? 'Starting…' : <>{totalAssigned} / {jobSummary['total']} questions &nbsp;<span className="fw600" style={{ color: isDone ? '#09B255' : '#4A67FF' }}>{overallPct}%</span></>}</span>
                                    {!isPriorityLoading && <span onClick={() => { setJobSummary(null); setSectionProgress({}); setSectionOrder([]); }} className="fs12px cp" style={{ color: '#AAACB5' }}>Clear</span>}
                                </div>
                            </div>
                            {/* Overall progress bar */}
                            <div style={{ height: '6px', background: '#E8EBF5' }}>
                                <div style={{ width: `${overallPct}%`, height: '100%', background: isDone ? '#09B255' : '#4A67FF', transition: 'width 0.4s ease', borderRadius: '0 3px 3px 0' }} />
                            </div>
                            {/* Section rows */}
                            <div style={{ maxHeight: '320px', overflowY: 'auto', padding: '0.75rem 1.25rem' }}>
                                {sectionOrder.map(sid => {
                                    const sec = sectionProgress[sid];
                                    if (!sec) return null;
                                    const pct = sec['percentage'] || 0;
                                    const barFill = pct === 100 ? '#09B255' : '#4A67FF';
                                    const pctColor = pct === 100 ? '#09B255' : pct > 0 ? '#4A67FF' : '#AAACB5';
                                    return (
                                        <div key={sid} className="df aic mb0-5rem" style={{ gap: '12px' }}>
                                            <span className="fs13px" style={{ color: '#151313', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sec['sectionName']}</span>
                                            <span className="fs12px" style={{ color: '#AAACB5', whiteSpace: 'nowrap', minWidth: '90px', textAlign: 'right' }}>{sec['objective']} / {sec['tier']}</span>
                                            <div style={{ flex: '0 0 140px', height: '8px', background: '#E8EBF5', borderRadius: '4px', overflow: 'hidden' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', background: barFill, borderRadius: '4px', transition: 'width 0.3s ease' }} />
                                            </div>
                                            <span className="fs13px fw600" style={{ color: pctColor, minWidth: '36px', textAlign: 'right' }}>{pct}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}
                <form onSubmit={handleSubmit(onSubmitForm)} className="dg fdc gg2rem">
                    {/* BASIC DETAILS */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Basic details</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>Name, key and routing</span>
                        </div>
                        <div className="dg gg1rem module-form-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
                            {/* Module Name */}
                            <div className="df fdc jcfs aifs">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Module Name *</label>
                                <input
                                    type="text"
                                    {...register('moduleName')}
                                    className={`br5px p0-5rem bss bw1px w100 ${errors.moduleName ? 'bo12' : 'bo5'}`}
                                    placeholder="Enter module name"
                                />
                                {errors.moduleName && (
                                    <small className="fs11px c12 mt0-25rem">{errors.moduleName.message}</small>
                                )}
                            </div>

                            {/* Module Key - Editable */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">
                                    Module Key <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={watchedValues.moduleKey || ''}
                                    onChange={handleModuleKeyChange}
                                    className={`br5px p0-5rem bss bw1px w100 ${errors.moduleKey ? 'bo12' : 'bo5'}`}
                                    style={{
                                        textTransform: 'uppercase',
                                        fontFamily: 'monospace',
                                        fontWeight: '500',
                                        color: '#333'
                                    }}
                                    placeholder="Enter module key (A-Z, 0-9)"
                                    maxLength={16}
                                />
                                {errors.moduleKey ? (
                                    <small className="fs11px c12 mt0-25rem">{errors.moduleKey.message}</small>
                                ) : (
                                    <small className="fs11px c15 op60 mt0-25rem" style={{ color: '#666' }}>
                                        Only letters and numbers allowed (A-Z, 0-9). Max 16 characters.
                                    </small>
                                )}
                            </div>

                            {/* Module Slug */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Module Slug</label>
                                <input
                                    type="text"
                                    {...register('slug')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{
                                        fontFamily: 'monospace',
                                        fontWeight: '400',
                                        color: '#333'
                                    }}
                                    placeholder="enter-module-slug"
                                />
                                <small className="fs11px c15 op60 mt0-25rem" style={{ color: '#666' }}>
                                    URL-friendly identifier. E.g., "repository" or "data-repository"
                                </small>
                            </div>

                            {/* Module sub title */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Tag</label>
                                <input
                                    type="text"
                                    {...register('moduleSubTitle')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    placeholder="Enter module tag"
                                />
                            </div>
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Tag colour</label>
                                <div className="df aic" style={{ gap: '0.5rem' }}>
                                    <input
                                        type="color"
                                        aria-label="Pick tag colour"
                                        value={
                                            typeof watchedValues.moduleTagColor === 'string'
                                                && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(watchedValues.moduleTagColor.trim())
                                                ? watchedValues.moduleTagColor.trim()
                                                : '#AAACB5'
                                        }
                                        onChange={(e) => setValue('moduleTagColor', e.target.value, { shouldValidate: true })}
                                        className="br5px oh cp bss bw1px bo5"
                                        style={{ width: '2.75rem', height: '2.25rem', padding: 0, backgroundColor: '#fff' }}
                                    />
                                    <input
                                        type="text"
                                        {...register('moduleTagColor')}
                                        className={`br5px p0-5rem bss bw1px w100 ${errors.moduleTagColor ? 'bo12' : 'bo5'}`}
                                        placeholder="#4A67FF (optional)"
                                    />
                                </div>
                                {errors.moduleTagColor ? (
                                    <small className="fs11px c12 mt0-25rem">{errors.moduleTagColor.message}</small>
                                ) : (
                                    <small className="fs11px c15 op60 mt0-25rem" style={{ color: '#666' }}>
                                        Leave blank for default tag styling on the dashboard.
                                    </small>
                                )}
                                <button
                                    type="button"
                                    className="fs11px fw500 c3 cp td300 tal mt0-25rem"
                                    style={{ background: 'none', border: 'none', padding: 0 }}
                                    onClick={() => setValue('moduleTagColor', '', { shouldValidate: true })}
                                >
                                    Reset to default colour
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* CONTENT & VISUALS */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Content & visuals</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>What this module looks like and says</span>
                        </div>
                        <div className="dg gg1rem module-form-grid" style={{ gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)' }}>
                            {/* Module Description */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Module Description</label>
                                <textarea
                                    {...register('moduleDescription')}
                                    className={`br5px p0-5rem bss bw1px w100 ${errors.moduleDescription ? 'bo12' : 'bo5'}`}
                                    placeholder="Enter module description"
                                    rows={4}
                                />
                                {errors.moduleDescription && (
                                    <small className="fs11px c12 mt0-25rem">{errors.moduleDescription.message}</small>
                                )}
                            </div>

                            {/* Module Image Upload */}
                            <div className="df fdc">
                                <ImageUploader
                                    imageURL={watchedValues.imageURL}
                                    onImageChange={(url) => setValue('imageURL', url)}
                                    label="Module Image"
                                />
                            </div>
                        </div>
                    </div>

                    {/* BEHAVIOUR & PLACEMENT */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Behaviour & placement</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>Where this module appears in the product</span>
                        </div>
                        <div className="df fdc">
                            {/* Module Type */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Module Type *</label>
                                <select
                                    {...register('type')}
                                    className={`br5px p0-5rem bss bw1px w100 ${errors.type ? 'bo12' : 'bo5'}`}
                                    style={{
                                        padding: '0.5rem',
                                        fontSize: '0.95rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="common">Common (Available for all companies)</option>
                                    <option value="unique">Dashboard</option>
                                    <option disabled value="partner">Partner</option>
                                    <option disabled value="internal">Internal</option>
                                    <option disabled value="external">External</option>
                                </select>
                                {errors.type ? (
                                    <small className="fs11px c12 mt0-25rem">{errors.type.message}</small>
                                ) : (
                                    <small className="fs11px c15 op60 mt0-25rem">Determines module availability scope</small>
                                )}
                            </div>

                            {/* Status */}
                            <div
                                className="df fww gg0-75rem br8px bss bo5 bw1px p0-75rem"
                                style={{ minHeight: "42px", alignContent: "center" }}
                            >
                                <div className="df aic bg1 br5px p0-5rem" style={{ minHeight: "32px" }}>
                                    <input
                                        type="checkbox"
                                        {...register('isActive')}
                                        className="mr0-5rem"
                                        id="isActive"
                                    />
                                    <label htmlFor="isActive" className="fs0-9rem fw500 c15 cp">Active Module</label>
                                </div>
                                <div className="df aic bg1 br5px p0-5rem" style={{ minHeight: "32px" }}>
                                    <input
                                        type="checkbox"
                                        {...register('isReportModule')}
                                        className="mr0-5rem"
                                        id="isReportModule"
                                    />
                                    <label htmlFor="isReportModule" className="fs0-9rem fw500 c15 cp">Is Report Module?</label>
                                </div>
                                <div className="df aic bg1 br5px p0-5rem" style={{ minHeight: "32px" }}>
                                    <input
                                        type="checkbox"
                                        {...register('isCustomSection')}
                                        className="mr0-5rem"
                                        id="isCustomSection"
                                    />
                                    <label htmlFor="isCustomSection" className="fs0-9rem fw500 c15 cp">Allow Custom Sections</label>
                                </div>
                                <div className="df aic bg1 br5px p0-5rem" style={{ minHeight: "32px" }}>
                                    <input
                                        type="checkbox"
                                        {...register('isDirectorsModule')}
                                        className="mr0-5rem"
                                        id="isDirectorsModule"
                                    />
                                    <label htmlFor="isDirectorsModule" className="fs0-9rem fw500 c15 cp">Directors Module (per-director answers)</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MODULE MENUS */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Module menus</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>Configure dynamic menu items such as &quot;How it works?&quot;, &quot;Help&quot; or &quot;What is it?&quot; for this module.</span>
                        </div>
                        <div className="df fdc" style={{ gap: '0.75rem' }}>
                            {(menusConfig || []).length === 0 && (
                                <p className="fs0-85rem c15 op70 m0">
                                    No custom menus configured yet. Use &quot;Add menu&quot; to create one.
                                </p>
                            )}

                            {(menusConfig || []).map((menu) => {
                                const isExpanded = expandedMenuKey === menu.key;
                                return (
                                    <div
                                        key={menu.key}
                                        className="br8px bss bo5 bw1px p0-75rem bg1"
                                        style={{ transition: 'all 0.2s ease' }}
                                    >
                                        <button
                                            type="button"
                                            className="df jcsb aic w100 cp"
                                            onClick={() => setExpandedMenuKey(isExpanded ? null : menu.key)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                padding: '0.5rem',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            <div className="df aic" style={{ gap: '0.5rem' }}>
                                                <span className="fw500 fs0-95rem c15">
                                                    {menu.label || 'Untitled menu'}
                                                </span>
                                                {menu.isActive === false && (
                                                    <span className="fs0-7rem br10px px0-5rem py0-15rem" style={{ background: '#ffecec', color: '#c62828' }}>
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                            <div className="df aic" style={{ gap: '0.5rem' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMenu(menu.key)}
                                                    className="fs0-8rem cp df aic jcc"
                                                    style={{ border: 'none', background: 'transparent', color: '#c62828', alignSelf: 'flex-start' }}
                                                >
                                                    <Delete color="error" />
                                                </button>
                                                <span className="df aic jcc" style={{ color: '#666' }}>
                                                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                </span>
                                            </div>
                                        </button>

                                        {isExpanded && (
                                            <div
                                                className="mt0-75rem dg px1rem"
                                                style={{
                                                    gap: '1.25rem',
                                                    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)'
                                                }}
                                            >
                                                {/* Left column: text and behaviour */}
                                                <div className="df fdc" style={{ gap: '0.75rem' }}>
                                                    <div className="df fdc">
                                                        <label className="fs0-85rem fw600 c15 mb0-25rem">Menu label</label>
                                                        <input
                                                            type="text"
                                                            value={menu.label || ''}
                                                            onChange={(e) => handleMenuChange(menu.key, 'label', e.target.value)}
                                                            className="br5px p0-5rem bo5 bss bw1px w100"
                                                            placeholder="e.g. How It Works?"
                                                        />
                                                    </div>
                                                    <div className="df aic jcfs" style={{ gap: '0.5rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={menu.isActive !== false}
                                                            onChange={(e) => handleMenuChange(menu.key, 'isActive', e.target.checked)}
                                                            id={`menu-active-${menu.key}`}
                                                        />
                                                        <label htmlFor={`menu-active-${menu.key}`} className="fs0-85rem fw500 c15 cp">
                                                            Active menu
                                                        </label>
                                                    </div>
                                                    <div className="df gg1rem" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>

                                                        <div className="df fdc">
                                                            <label className="fs0-85rem fw600 c15 mb0-25rem">Action type</label>
                                                            <select
                                                                value={menu.actionType || 'navigate'}
                                                                onChange={(e) => handleMenuChange(menu.key, 'actionType', e.target.value)}
                                                                className="br5px p0-5rem bo5 bss bw1px w100"
                                                            >
                                                                <option value="navigate">Navigate (open URL)</option>
                                                                <option value="modal">Open modal</option>
                                                            </select>
                                                        </div>

                                                        {menu.actionType === 'modal' && (
                                                            <div className="df fdc">
                                                                <label className="fs0-85rem fw600 c15 mb0-25rem">Modal display type</label>
                                                                <select
                                                                    value={menu.modalDisplayType || 'contentAndImage'}
                                                                    onChange={(e) => handleMenuChange(menu.key, 'modalDisplayType', e.target.value)}
                                                                    className="br5px p0-5rem bo5 bss bw1px w100"
                                                                >
                                                                    {/* <option disabled value="contentAndImage">Content + Image</option> */}
                                                                    <option value="imageOnly">Image Only</option>
                                                                </select>
                                                            </div>
                                                        )}
                                                        {/* {menu.actionType === 'modal' && (menu.modalDisplayType || 'contentAndImage') !== 'imageOnly' && (
                                                            <div className="df fdc">
                                                                <label className="fs0-85rem fw600 c15 mb0-25rem">Modal Image position</label>
                                                                <select
                                                                    value={menu.modalImagePosition || 'right'}
                                                                    onChange={(e) => handleMenuChange(menu.key, 'modalImagePosition', e.target.value)}
                                                                    className="br5px p0-5rem bo5 bss bw1px w100"
                                                                >
                                                                    <option value="left">Image on the left</option>
                                                                    <option value="right">Image on the right</option>
                                                                </select>
                                                            </div>
                                                        )} */}
                                                    </div>

                                                    {menu.actionType === 'navigate' && (
                                                        <div className="df fdc">
                                                            <label className="fs0-85rem fw600 c15 mb0-25rem">Target URL</label>
                                                            <input
                                                                type="text"
                                                                value={menu.navigateUrl || ''}
                                                                onChange={(e) => handleMenuChange(menu.key, 'navigateUrl', e.target.value)}
                                                                className="br5px p0-5rem bo5 bss bw1px w100"
                                                                placeholder="https://example.com/help"
                                                            />
                                                            <small className="fs0-75rem c15 op60 mt0-25rem">
                                                                Must be a full URL (including https://). The link will open in a new tab.
                                                            </small>
                                                        </div>
                                                    )}

                                                    {menu.actionType === 'modal' && (
                                                        <div className="df fdc">
                                                            <label className="fs0-85rem fw600 c15 mb0-25rem">Modal title</label>
                                                            <input
                                                                type="text"
                                                                value={menu.modalTitle || ''}
                                                                onChange={(e) => handleMenuChange(menu.key, 'modalTitle', e.target.value)}
                                                                className="br5px p0-5rem bo5 bss bw1px w100"
                                                                placeholder="Enter modal title"
                                                            />
                                                        </div>
                                                    )}

                                                    {/* {menu.actionType === 'modal' && (menu.modalDisplayType || 'contentAndImage') !== 'imageOnly' && (
                                                        <div className="df fdc">
                                                            <label className="fs0-85rem fw600 c15 mb0-25rem">Modal content (rich text)</label>
                                                            <ReactQuill
                                                                theme="snow"
                                                                value={menu.modalContent || ''}
                                                                onChange={(value) => handleMenuChange(menu.key, 'modalContent', value)}
                                                                placeholder="Enter modal content (formatted text)"
                                                            />
                                                        </div>
                                                    )} */}
                                                    {menu.actionType === 'modal' && (
                                                        <div className="mt1rem">
                                                            <ImageUploader
                                                                imageURL={menu.modalImageURL}
                                                                onImageChange={(url) => handleMenuChange(menu.key, 'modalImageURL', url)}
                                                                label="Modal image"
                                                            />
                                                        </div>
                                                    )}


                                                </div>

                                                {/* Right column: icons/images */}
                                                <div className="df fdc" style={{ gap: '0.75rem' }}>
                                                    <ImageUploader
                                                        imageURL={menu.iconURL}
                                                        onImageChange={(url) => handleMenuChange(menu.key, 'iconURL', url)}
                                                        label="Menu icon"
                                                    />

                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <div className="df aic jcfe">
                                <button
                                    type="button"
                                    onClick={handleAddMenu}
                                    className="df aic jcc cp mt0-5rem"
                                    style={{
                                        border: 'none',
                                        background: '#4CAF50',
                                        color: 'white',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        gap: '8px',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 6px rgba(76, 175, 80, 0.25)'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.background = '#45a049';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.35)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.background = '#4CAF50';
                                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(76, 175, 80, 0.25)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <Add style={{ fontSize: '20px' }} />
                                    Add menu
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* SUBSCRIPTION & PRICING */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Subscription & pricing</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>Per-tier pricing for this module</span>
                        </div>
                        <div className="dg gg1rem module-form-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
                            {/* Is Paid */}
                            <div className="df aic" style={{ gridColumn: '1 / -1' }}>
                                <input
                                    type="checkbox"
                                    {...register('isPaid')}
                                    className="mr0-5rem"
                                    id="isPaid"
                                />
                                <label htmlFor="isPaid" className="fs0-9rem fw500 c15 cp">Paid module</label>
                                <small className="fs11px c15 op60 ml0-5rem">When enabled, this module requires payment</small>
                            </div>

                            {/* Price - shown when isPaid */}
                            {watchedValues.isPaid && (
                                <>
                                    <div className="df fdc">
                                        <label className="fs0-9rem fw600 c15 mb0-5rem">Price Amount (£) *</label>
                                        <input
                                            type={isEdit ? 'text' : 'number'}
                                            min="0.01"
                                            step="0.01"
                                            {...register('price')}
                                            className={`br5px p0-5rem bss bw1px w100 ${errors.price ? 'bo12' : 'bo5'}`}
                                            placeholder="e.g. 99"
                                            readOnly={isEdit}
                                            style={isEdit ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                        />
                                        {errors.price ? (
                                            <small className="fs11px c12 mt0-25rem">{errors.price.message}</small>
                                        ) : (
                                            <small className="fs11px c15 op60 mt0-25rem">
                                                {isEdit ? 'Price is set at creation — change via Stripe dashboard.' : 'Amount in GBP. A Stripe product will be created automatically.'}
                                            </small>
                                        )}
                                    </div>
                                    {isEdit && moduleData?.stripeProductPriceId && (
                                        <div className="df fdc">
                                            <label className="fs0-9rem fw600 c15 mb0-5rem">Stripe Price ID</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={moduleData.stripeProductPriceId}
                                                className="br5px p0-5rem bss bw1px w100 bo5"
                                                style={{ background: '#f5f5f5', cursor: 'not-allowed', fontFamily: 'monospace', fontSize: '0.8rem' }}
                                            />
                                            <small className="fs11px c15 op60 mt0-25rem">Auto-generated Stripe price reference</small>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* CATEGORIES */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Categories</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>Module behaviour and output configuration</span>
                        </div>
                        <div className="dg gg1rem" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)' }}>

                            {/* Duration */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Duration</label>
                                <select
                                    {...register('duration')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="isReady">Is Ready</option>
                                    <option value="1 mins">1 mins</option>
                                    <option value="2 mins">2 mins</option>
                                    <option value="3mins">3 mins</option>
                                    <option value="5mins">5 mins</option>
                                    <option value="10mins">10 mins</option>
                                    <option value="15mins">15 mins</option>
                                    <option value="30mins">30 mins</option>
                                    <option value="2 days">2 days</option>
                                    <option value="NIL">NIL</option>
                                </select>
                                <small className="fs11px c15 op60 mt0-25rem">Estimated time to complete</small>
                            </div>

                            {/* Completeness */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Completeness</label>
                                <select
                                    {...register('completeness')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Risks */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Risks</label>
                                <select
                                    {...register('risks')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Pass/Fail */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Pass / Fail</label>
                                <select
                                    {...register('passFail')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Certification */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Certification</label>
                                <select
                                    {...register('certification')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Report */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Report</label>
                                <select
                                    {...register('report')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Workflow Report */}
                            <div className="df fdc">
                                <label className="fs0-9rem fw600 c15 mb0-5rem">Workflow Report</label>
                                <select
                                    {...register('workflowReport')}
                                    className="br5px p0-5rem bss bw1px w100 bo5"
                                    style={{ padding: '0.5rem', fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                        </div>
                    </div>

                    {/* Certification Rules Builder — shown only when certification is enabled */}
                    {watch('certification') === 'Yes' && (
                        <div className="bss bw1px br8px p1-5rem" style={{ gridColumn: '1 / -1', borderColor: '#e5e7eb', marginTop: '0.5rem' }}>
                            <CertificationRulesBuilder
                                rules={certificationRules}
                                onChange={setCertificationRules}
                            />
                        </div>
                    )}

                    {/* Content Generation Mode — only shown on Create (not Edit) */}
                    {!watchedValues.isReportModule && (
                        <div
                            className="br10px bss bw1px p1-5rem"
                            style={{
                                gridColumn: '1 / -1',
                                borderColor: generationMode === 'ai' ? '#4c6ef5' : generationMode === 'upload' ? '#40dda1' : '#e2e8f0'
                            }}
                        >
                            <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
                                <div>
                                    <h3 className="fs1rem fw600 c15 m0">Content Generation</h3>
                                    <p className="fs0-8rem c15 m0 mt0-25rem" style={{ opacity: 0.6 }}>
                                        Choose how sections and questions are created for this module.
                                    </p>
                                </div>

                                {/* Toggle pills */}
                                <div
                                    className="df bg1 br999 bss bo5 bw1px p0-25rem"
                                    style={{ gap: '0.25rem', alignSelf: 'flex-start' }}
                                >
                                    {[
                                        { key: 'manual', label: 'Manual' },
                                        { key: 'ai', label: 'AI Generate' },
                                        { key: 'upload', label: 'File Upload' },
                                    ].map(({ key, label }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setGenerationMode(key)}
                                            className={`br20px px0-75rem py0-5rem fs0-8rem fw600 td250 cp ${generationMode === key ? 'bg3 c1' : 'bg1 c15'}`}
                                            style={{ border: 'none', minWidth: '100px' }}
                                        >
                                            {key === 'ai' && <span style={{ marginRight: '0.3rem' }}>✦</span>}
                                            {key === 'upload' && <i className="fas fa-file-upload" style={{ marginRight: '0.35rem', fontSize: '0.75rem' }} />}
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* File Upload panel */}
                            {generationMode === 'upload' && (
                                <div>
                                    <div
                                        className="p1rem br8px mb1rem"
                                        style={{ background: '#e8fdf3', border: '1px solid #a5d6a7' }}
                                    >
                                        <p className="fs0-85rem fw500 m0" style={{ color: '#1b5e20' }}>
                                            Upload an xlsx, xls, or csv file. Required columns: Section, Question. Supported columns: Sub-section, Question Type, Tool Tip, Tool Box, Risk Description, Recommendation, Risk Score, Attachment Required, Options, Info if Yes, Info if No, Risk if Yes, Risk if No, Reference.
                                            Click <strong>Download Sample</strong> above to get the template. After parsing, select which objective × tier combinations to populate.
                                        </p>
                                    </div>
                                    <ExcelUpload
                                        moduleKey={watchedValues.moduleKey}
                                        parsedData={excelParsedData}
                                        importedVariants={excelImportedVariants}
                                        onParsed={(data) => {
                                            setExcelParsedData(data);
                                            setExcelImportedVariants([]);
                                            setExcelVariantModalOpen(true);
                                        }}
                                        onApply={() => setExcelVariantModalOpen(true)}
                                        onReselect={() => setExcelVariantModalOpen(true)}
                                    />
                                    <ExcelVariantModal
                                        open={excelVariantModalOpen}
                                        parsedData={excelParsedData}
                                        moduleKey={watchedValues.moduleKey}
                                        onConfirm={(selectedVariants) => handleExcelImport(selectedVariants, excelParsedData)}
                                        onClose={() => setExcelVariantModalOpen(false)}
                                    />
                                </div>
                            )}

                            {/* AI config options */}
                            {generationMode === 'ai' && (
                                <div>
                                    <div
                                        className="p1rem br8px mb1rem"
                                        style={{ background: '#eef2ff', border: '1px solid #c7d2fe' }}
                                    >
                                        <p className="fs0-85rem fw500 m0" style={{ color: '#3730a3' }}>
                                            ✦ AI will automatically generate sections and questions for all 9
                                            combinations (3 objectives × 3 tiers) using GPT-4. You can edit
                                            the content after generation completes.
                                        </p>
                                    </div>

                                    <div className="dg gg1rem" style={{ gridTemplateColumns: '1fr 1fr' }}>
                                        <div className="df fdc">
                                            <label className="fs0-85rem fw600 c15 mb0-5rem">
                                                Sections per combination
                                            </label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={15}
                                                value={aiConfig.sectionsPerCombo}
                                                onChange={(e) =>
                                                    setAiConfig((prev) => ({
                                                        ...prev,
                                                        sectionsPerCombo: Math.min(15, Math.max(1, parseInt(e.target.value) || 5)),
                                                    }))
                                                }
                                                className="br5px p0-5rem bss bw1px bo5 w100"
                                            />
                                            <small className="fs11px c15 op60 mt0-25rem">
                                                1–15 sections per objective × tier combo (default 5)
                                            </small>
                                        </div>

                                        <div className="df fdc">
                                            <label className="fs0-85rem fw600 c15 mb0-5rem">
                                                Questions per section
                                            </label>
                                            <input
                                                type="number"
                                                min={5}
                                                max={15}
                                                value={aiConfig.questionsPerSection}
                                                onChange={(e) =>
                                                    setAiConfig((prev) => ({
                                                        ...prev,
                                                        questionsPerSection: Math.min(15, Math.max(5, parseInt(e.target.value) || 10)),
                                                    }))
                                                }
                                                className="br5px p0-5rem bss bw1px bo5 w100"
                                            />
                                            <small className="fs11px c15 op60 mt0-25rem">
                                                5–15 questions per section (default 10)
                                            </small>
                                        </div>
                                    </div>

                                    <p className="fs0-78rem c15 mt1rem m0" style={{ opacity: 0.5 }}>
                                        Estimated output: {9 * aiConfig.sectionsPerCombo} sections
                                        and {9 * aiConfig.sectionsPerCombo * aiConfig.questionsPerSection} questions.
                                        Generation runs in the background (~5–15 minutes).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sections Management OR special module flows OR Report Module card assignment */}
                    {isInvestorLead ? (
                        <div className="section-container" style={{ marginTop: '3rem', gridColumn: '1 / -1' }}>
                            <h2 className="fs1-4rem fw600 c15 mb1rem" style={{ letterSpacing: '-0.02em', margin: 0 }}>
                                Investment Objective
                            </h2>
                            <p className="fs0-85rem c15 op70 mb1-5rem">
                                Edit the Investor Lead section (DEPF008) and its questions directly. Each question is saved individually.
                            </p>
                            <InvestorLeadSection />
                        </div>
                    ) : isInvestmentReadiness ? (
                        <div className="section-container" style={{ marginTop: '3rem', gridColumn: '1 / -1' }}>
                            <h2 className="fs1-4rem fw600 c15 mb1rem" style={{ letterSpacing: '-0.02em', margin: 0 }}>
                                Investment Readiness Questions
                            </h2>
                            <p className="fs0-85rem c15 op70 mb1-5rem">
                                Edit the 25 Investment Readiness questions directly. Each question is saved individually.
                            </p>
                            <InvestmentReadinessQuestions ref={irQuestionsRef} />
                        </div>
                    ) : isExitReadiness ? (
                        <div className="section-container" style={{ marginTop: '3rem', gridColumn: '1 / -1' }}>
                            <h2 className="fs1-4rem fw600 c15 mb1rem" style={{ letterSpacing: '-0.02em', margin: 0 }}>
                                Exit Readiness Questions
                            </h2>
                            <p className="fs0-85rem c15 op70 mb1-5rem">
                                Edit Exit Readiness questions directly. Each question is saved individually.
                            </p>
                            <ExitReadinessQuestions />
                        </div>
                    ) : watchedValues.isReportModule ? (
                        <div className="section-container" style={{ marginTop: '3rem', gridColumn: '1 / -1' }}>
                            <h2 className="fs1-4rem fw600 c15 mb1rem" style={{ letterSpacing: '-0.02em', margin: 0 }}>
                                Report Module — Card Assignment
                            </h2>
                            <p className="fs0-85rem c15 op70 mb1-5rem">
                                Assign a dashboard card per Company Objective and Subscription Tier, or select &quot;No card&quot; to hide this module for that combination.
                            </p>

                            {/* Objective tabs */}
                            <div className="mb1-5rem">
                                <div className="df jcsb aic mb0-5rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <span className="fs0-85rem fw600 c15">Company objective</span>
                                </div>
                                <div className="df bg1 br999 bss bo5 bw1px p0-25rem" style={{ flexWrap: 'wrap', gap: '0.25rem' }}>
                                    {[...COMPANY_OBJECTIVES].map((obj) => {
                                        const isActive = selectedObjective === obj;
                                        return (
                                            <button
                                                key={obj}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedObjective(obj);
                                                    setSelectedTier(obj === OBJECTIVE_ALL ? TIER_ALL : SUBSCRIPTION_TIERS[0]);
                                                }}
                                                className={`br20px px0-75rem py0-75rem fs0-75rem fw500 td250 cp ${isActive ? 'bg3 c1' : 'bg1 c15'}`}
                                                style={{ border: 'none', minWidth: '90px', textTransform: 'capitalize' }}
                                            >
                                                {obj === OBJECTIVE_ALL ? 'All objectives' : obj}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Tier tabs */}
                            {selectedObjective !== OBJECTIVE_ALL ? (
                                <div className="mb1-5rem">
                                    <div className="df jcsb aic mb0-5rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <span className="fs1rem fw600 c15">Subscription tier</span>
                                    </div>
                                    <div className="df" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {SUBSCRIPTION_TIERS.map((tier) => {
                                            const isActive = selectedTier === tier;
                                            return (
                                                <button
                                                    key={tier}
                                                    type="button"
                                                    onClick={() => setSelectedTier(tier)}
                                                    className={`br20px px1-5rem py0-75rem fs0-75rem fw500 td250 cp ${isActive ? 'bg7 c1' : 'bg1 c15'}`}
                                                    style={{ border: '1px solid var(--border, #e0e0e0)' }}
                                                >
                                                    {tier}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="mb1-5rem">
                                    <span className="fs0-85rem fw600 c15">Subscription tier</span>
                                    <p className="fs0-75rem c15 op70 mt0-25rem mb0">Applies to <strong>all tiers</strong> because &quot;All objectives&quot; is selected.</p>
                                </div>
                            )}

                            {/* Dashboard card selector for this objective/tier */}
                            <div className="df aic" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                                <span className="fs0-9rem fw600 c15">
                                    <strong>{selectedObjective}</strong> → <strong>{selectedTier}</strong>:
                                </span>
                                <select
                                    value={placementsByVariant[currentVariantKey] || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setPlacementsByVariant(prev => ({ ...prev, [currentVariantKey]: value }));
                                    }}
                                    className="br5px p0-5rem bss bw1px"
                                    style={{ cursor: 'pointer', minWidth: '220px', maxWidth: '100%' }}
                                >
                                    <option value="">No card</option>
                                    {(dashboardCards || []).map((card) => (
                                        <option key={card._id} value={card._id}>
                                            {card.title || card.subTitle || card._id}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className="section-container" style={{ marginTop: '3rem', gridColumn: '1 / -1' }}>
                            <h2 className="fs1-4rem fw600 c15 mb1rem" style={{ letterSpacing: '-0.02em', margin: 0 }}>
                                Module Sections
                            </h2>
                            <p className="fs0-85rem c15 op70 mb1-5rem">
                                Add sections per Company Objective and Subscription Tier. Each combination (e.g. Investment → Pro) can have its own sections.
                            </p>

                            {/* Objective tabs */}
                            <div className="mb1-5rem">
                                <div className="df jcsb aic mb0-5rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <span className="fs0-85rem fw600 c15">Company objective</span>
                                    <span className="fs0-75rem c15 op60" style={{ minWidth: 0, maxWidth: '100%' }}>
                                        Choose the high–level goal this set of sections targets.
                                    </span>
                                </div>
                                <div
                                    className="df bg1 br999 bss bo5 bw1px p0-25rem"
                                    style={{ flexWrap: 'wrap', gap: '0.25rem' }}
                                >
                                    {[...COMPANY_OBJECTIVES].map((obj) => {
                                        const isActive = selectedObjective === obj;
                                        return (
                                            <button
                                                key={obj}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedObjective(obj);
                                                    setSelectedTier(obj === OBJECTIVE_ALL ? TIER_ALL : SUBSCRIPTION_TIERS[0]);
                                                }}
                                                className={`br20px px0-75rem py0-75rem fs0-75rem fw500 td250 cp ${isActive ? 'bg3 c1' : 'bg1 c15'
                                                    }`}
                                                style={{
                                                    border: 'none',
                                                    minWidth: '90px',
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {obj === OBJECTIVE_ALL ? 'All objectives' : obj}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Tier tabs (when objective is not All); when All, tier is All */}
                            {selectedObjective !== OBJECTIVE_ALL ? (
                                <div className="mb1-5rem">
                                    <div className="df jcsb aic mb0-5rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <span className="fs1rem fw600 c15">Subscription tier</span>
                                        <span className="fs0-75rem c15 op60" style={{ minWidth: 0 }}>
                                            Sections will only appear for companies on the selected tier.
                                        </span>
                                    </div>
                                    <div className="df" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {SUBSCRIPTION_TIERS.map((tier) => {
                                            const isActive = selectedTier === tier;
                                            return (
                                                <button
                                                    key={tier}
                                                    type="button"
                                                    onClick={() => setSelectedTier(tier)}
                                                    className={`br20px px1-5rem py0-75rem fs0-75rem fw500 td250 cp  ${isActive ? 'bg7 c1' : 'bg1 c15'
                                                        }`}
                                                    style={{ border: '1px solid var(--border, #e0e0e0)' }}
                                                >
                                                    {tier}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="mb1-5rem">
                                    <span className="fs0-85rem fw600 c15">Subscription tier</span>
                                    <p className="fs0-75rem c15 op70 mt0-25rem mb0">
                                        Applies to <strong>all tiers</strong> because &quot;All objectives&quot; is selected.
                                    </p>
                                </div>
                            )}

                            <div className="df jcsb aic mb1-5rem" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
                                <div className="df fdc aifs jcfs tal" style={{ minWidth: 0, flex: '1 1 200px' }}>
                                    <span className="fs1rem c15 w100 tal">
                                        <strong>{selectedObjective}</strong> → <strong>{selectedTier}</strong>
                                        {currentSections.length > 0 && ` (${currentSections.length} section${currentSections.length !== 1 ? 's' : ''})`}
                                    </span>
                                    {sectionErrors._form && (
                                        <small className="fs11px c12 mt0-25rem">{sectionErrors._form}</small>
                                    )}
                                </div>
                                <div className="df aic jcfe" style={{ gap: '0.75rem', flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>

                                    <label className="df aic cp" style={{ gap: '8px', userSelect: 'none' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!sectionRestrictionsByVariant[currentVariantKey]}
                                            onChange={(e) => {
                                                setSectionRestrictionsByVariant((prev) => ({
                                                    ...prev,
                                                    [currentVariantKey]: e.target.checked
                                                }));
                                            }}
                                        />
                                        <span className="fs0-85rem c15">
                                            Is Restricted for {selectedTier}?
                                        </span>
                                    </label>
                                    {/* Dashboard card for this objective/tier */}
                                    <select
                                        value={placementsByVariant[currentVariantKey] || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setPlacementsByVariant(prev => ({
                                                ...prev,
                                                [currentVariantKey]: value
                                            }));
                                        }}
                                        className="br5px p0-5rem bss bw1px"
                                        style={{ cursor: 'pointer', minWidth: '180px', maxWidth: '100%' }}
                                    >
                                        <option value="">No card (hidden for this combo)</option>
                                        {(dashboardCards || []).map((card) => (
                                            <option key={card._id} value={card._id}>
                                                {card.title || card.subTitle || card._id}
                                            </option>
                                        ))}
                                    </select>
                                    {cloneTargetOptions.length > 0 && currentSections.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={openBulkCloneModal}
                                            className="df aic jcc cp"
                                            style={{
                                                border: '1px solid #2196F3',
                                                background: 'white',
                                                color: '#2196F3',
                                                padding: '10px 16px',
                                                borderRadius: '8px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                gap: '6px',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.background = '#2196F315';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.background = 'white';
                                            }}
                                            title="Clone all sections to objective/tier targets"
                                        >
                                            <ContentCopy style={{ fontSize: '18px' }} />
                                            Clone sections
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={addSection}
                                        className="df aic jcc cp"
                                        style={{
                                            border: 'none',
                                            background: '#4CAF50',
                                            color: 'white',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            gap: '8px',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 2px 6px rgba(76, 175, 80, 0.25)'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.background = '#45a049';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.35)';
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.background = '#4CAF50';
                                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(76, 175, 80, 0.25)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <Add style={{ fontSize: '20px' }} />
                                        Add Section
                                    </button>
                                </div>
                            </div>

                            {/* Clone confirmation modal */}
                            {cloneModalOpen && (
                                <div
                                    style={{
                                        position: 'fixed',
                                        inset: 0,
                                        background: 'rgba(0,0,0,0.4)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 9999
                                    }}
                                    onClick={handleCloneCancel}
                                >
                                    <div
                                        className="bg1 br12px p2rem"
                                        style={{ maxWidth: '480px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {cloneView === 'select' ? (
                                            <>
                                                <h4 className="fs1-1rem fw600 c15 mb1rem" style={{ margin: 0 }}>
                                                    {cloneMode === 'bulk' ? 'Clone sections to objective/tier' : 'Clone section to objective/tier'}
                                                </h4>
                                                <p className="fs0-9rem c15 op70 mb1-5rem">
                                                    {cloneMode === 'bulk'
                                                        ? `Clone all ${currentSections.length} section(s) from ${selectedObjective} → ${selectedTier} into:`
                                                        : `Clone this section into which objective/tier target(s)?`}
                                                </p>
                                                <div className="df fdc" style={{ gap: '0.75rem', marginBottom: '1.5rem' }}>
                                                    {cloneTargetOptions.map((target) => (
                                                        <label key={target.key} className="df aic cp" style={{ gap: '0.5rem' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={cloneTargets.includes(target.key)}
                                                                onChange={() => toggleCloneTarget(target.key)}
                                                            />
                                                            <span className="fs0-95rem fw500">{target.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="df jcfe" style={{ gap: '0.75rem' }}>
                                                    <button
                                                        type="button"
                                                        onClick={handleCloneCancel}
                                                        className="br5px px1rem py0-5rem fw500"
                                                        style={{ border: '1px solid #ddd', background: 'white', color: '#333' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCloneConfirm(false)}
                                                        disabled={cloneTargets.length === 0}
                                                        className="br5px px1rem py0-5rem fw500"
                                                        style={{
                                                            border: 'none',
                                                            background: cloneTargets.length === 0 ? '#ccc' : '#2196F3',
                                                            color: 'white',
                                                            cursor: cloneTargets.length === 0 ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        Clone
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <h4 className="fs1-1rem fw600 c15 mb1rem" style={{ margin: 0 }}>
                                                    Section number conflict
                                                </h4>
                                                <div
                                                    className="br8px p1rem mb1-5rem"
                                                    style={{
                                                        background: '#fff8e6',
                                                        border: '1px solid #ffc107',
                                                        color: '#856404'
                                                    }}
                                                >
                                                    {cloneConflictConflicts.length === 1 ? (
                                                        <p className="fs0-9rem m0" style={{ lineHeight: 1.5 }}>
                                                            You are trying to clone <strong>{cloneConflictConflicts[0].sectionNo}</strong> into{' '}
                                                            <strong>{cloneConflictConflicts[0].objective} – {cloneConflictConflicts[0].tier}</strong>, but a section
                                                            with the same number (<strong>{cloneConflictConflicts[0].sectionNo}</strong>) already exists in{' '}
                                                            <strong>{cloneConflictConflicts[0].objective} – {cloneConflictConflicts[0].tier}</strong>.
                                                        </p>
                                                    ) : (
                                                        <p className="fs0-9rem m0" style={{ lineHeight: 1.5 }}>
                                                            The following sections already exist in the target tier(s):
                                                        </p>
                                                    )}
                                                    {cloneConflictConflicts.length > 1 && (
                                                        <ul className="fs0-9rem mt0-5rem mb0" style={{ paddingLeft: '1.25rem', lineHeight: 1.6 }}>
                                                            {cloneConflictConflicts.map((c, i) => (
                                                                <li key={i}>
                                                                    <strong>{c.sectionNo}</strong> in {c.objective} – {c.tier}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <p className="fs0-9rem mt1rem mb0" style={{ lineHeight: 1.5 }}>
                                                        Do you want to overwrite the existing section{cloneConflictConflicts.length !== 1 ? 's' : ''}, or cancel this operation?
                                                    </p>
                                                </div>
                                                <div className="df jcfe" style={{ gap: '0.75rem' }}>
                                                    <button
                                                        type="button"
                                                        onClick={handleCloneCancel}
                                                        className="br5px px1rem py0-5rem fw500"
                                                        style={{ border: '1px solid #ddd', background: 'white', color: '#333' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleCloneOverwrite}
                                                        className="br5px px1rem py0-5rem fw500"
                                                        style={{
                                                            border: 'none',
                                                            background: '#2196F3',
                                                            color: 'white',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Overwrite
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {currentSections.length === 0 && (
                                <div className="br12px p3rem tac" style={{
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                    border: '2px dashed #dee2e6'
                                }}>
                                    <div className="df aic jcc" style={{ marginBottom: '1rem' }}>
                                        <FolderSpecial style={{ fontSize: '64px', color: '#dee2e6' }} />
                                    </div>
                                    <p className="fs1-1rem fw500 mb0-75rem" style={{ color: '#495057', margin: '0 0 0.5rem 0' }}>
                                        No sections for {selectedObjective} → {selectedTier}
                                    </p>
                                    <p className="fs0-9rem" style={{ color: '#6c757d', margin: 0 }}>
                                        Click &quot;Add Section&quot; to add a section for this objective and tier
                                    </p>
                                </div>
                            )}

                            {currentSections.map((section, sIdx) => (
                                <SectionForm
                                    key={`${effectiveVariantKey}-${sIdx}`}
                                    section={section}
                                    index={sIdx}
                                    moduleKey={watchedValues.moduleKey}
                                    isExpanded={expandedSectionIndex === sIdx}
                                    onToggleExpand={() => setExpandedSectionIndex(expandedSectionIndex === sIdx ? null : sIdx)}
                                    onUpdate={(updatedSection) => updateSection(effectiveVariantKey, sIdx, updatedSection)}
                                    onRemove={() => removeSection(effectiveVariantKey, sIdx)}
                                    isDeletePending={pendingSectionDeleteKeys.has(
                                        section?._id
                                            ? `id:${String(section._id)}`
                                            : `variant:${effectiveVariantKey}:sectionNo:${String(section?.sectionNo || '').trim()}`
                                    )}
                                    onDeleteQuestion={async (questionId) => {
                                        const debounceKey = `question-${questionId || `${effectiveVariantKey}-${sIdx}`}`;
                                        if (acquireDeleteLock(debounceKey)) return false;
                                        if (!isEdit || !questionId) return true;
                                        const response = await deleteRequest(`/modules/temp-questions/${questionId}`);
                                        return response?.code === 200;
                                    }}
                                    onClone={cloneTargetOptions.length > 0 ? () => openSingleCloneModal(section) : undefined}
                                    errors={sectionErrors[`${effectiveVariantKey}-${sIdx}`] || {}}
                                />
                            ))}
                        </div>
                    )}



                    {/* Action Buttons */}
                    <div className="df jcfe gg1rem mt1rem" style={{ gap: '1rem', gridColumn: '1 / -1', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="bg1 bo4 bss bw1px br5px px1rem py0-5rem fw600 c4 td250 cp"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg3 bo3 bss bw1px br5px px1rem py0-5rem fw600 c1 td250 cp"
                        >
                            {isEdit ? 'Update Module' : 'Create Module'}
                        </button>
                    </div>
                    {/* Live Preview */}
                    <div className="br10px bss bo5 bw1px p1-5rem" style={{ gridColumn: '1 / -1' }}>
                        <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                            <h3 className="fs1-1rem fw600 c15 m0">Module card preview</h3>
                            <span className="fs0-8rem c15 op60" style={{ minWidth: 0 }}>Updates live as you edit</span>
                        </div>
                        <ModuleCardPreview moduleData={watchedValues} />
                    </div>
                </form>
                {/* <QuestionsLoop tab={tab} /> */}
            </div>

            <ModalWrapper modalType="priorityConfirmModal">
                <h1 className="c15 fs1-75rem fw600 tac mb1rem">Priorities Already Set</h1>
                <p className="fs1rem fw400 c15 op70 tac mb1-5rem">
                    AI priorities have already been generated for this module. Do you want to regenerate them?
                </p>
                <Button text="Yes, Regenerate" onClick={handleConfirmPriorityRegen} btnType="default" customClassName="w100" />
                <div onClick={() => dispatch(toggleModal({ open: '' }))} className="w100 tac mt0-5rem">
                    <span className="fs14px cp" style={{ color: '#15131380' }}>Cancel</span>
                </div>
            </ModalWrapper>
        </div>
    );
};

export default ModuleForm;
